import { DialogComponent } from '@theme/dialog';

/**
 * Product share custom element.
 *
 * - share()       Mobile (< 750px) uses the Web Share API; desktop opens
 *                 the in-theme dialog so the experience matches the brand.
 * - copyLink()    Writes the share URL to the clipboard and swaps the
 *                 button label to a localised "copied" state for 2 s.
 *
 * @extends DialogComponent
 */
class ProductShareComponent extends DialogComponent {
  #copyResetTimer = null;

  async share() {
    const data = {
      title: this.dataset.shareTitle || document.title,
      text: this.dataset.shareText || '',
      url: this.dataset.shareUrl || window.location.href,
    };

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const canUseNativeShare =
      isMobile &&
      typeof navigator !== 'undefined' &&
      typeof navigator.share === 'function' &&
      (typeof navigator.canShare !== 'function' || navigator.canShare(data));

    if (canUseNativeShare) {
      try {
        await navigator.share(data);
      } catch {
        /* swallow */
      }
      return;
    }

    this.showDialog();
  }

  async copyLink() {
    // Prefer the copy-specific URL (carries utm_source=copy_link); fall
    // back to the generic share URL if not provided.
    const url = this.dataset.copyUrl || this.dataset.shareUrl;
    if (!url) return;

    try {
      await navigator.clipboard.writeText(url);
    } catch {
      return;
    }

    const idle = this.querySelector('[data-copy-idle]');
    const done = this.querySelector('[data-copy-done]');
    idle?.classList.add('visually-hidden');
    done?.classList.remove('visually-hidden');

    clearTimeout(this.#copyResetTimer);
    this.#copyResetTimer = setTimeout(() => {
      idle?.classList.remove('visually-hidden');
      done?.classList.add('visually-hidden');
    }, 2000);
  }
}

if (!customElements.get('product-share-component')) {
  customElements.define('product-share-component', ProductShareComponent);
}
